import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import {
  getPermissionDeniedMessage, getWriteErrorMessage, OUTCOME_UNKNOWN_MESSAGE, PamGrid,
  shouldReloadAfterWriteError, usePermission, VERSION_CONFLICT_MESSAGE, withLoadedModifiedDate,
} from '@1823-partners/core';
import { fetchRatingsInputs, saveRatingsInputs } from '../api';
import { isStaleWrite, rebaseEdit } from '../utils/modifiedDates';

const INPUT_FIELDS = ['INPUT_ID', 'KIND', 'VALUE', 'ABBREVIATION', 'IS_ACTIVE'];
const inputRecord = (row) =>
  Object.fromEntries(INPUT_FIELDS.map((field) => [field, row[field] ?? null]));
const errorMessage = (error) => error.response?.data?.error || error.message || 'Request failed.';

export default function RatingsInputs({ onSaved }) {
  const [rows, setRows] = useState([]);
  // Read-only until the permission center confirms the grant.
  const canWrite = usePermission('secmaster.securities:write');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmation, setConfirmation] = useState(null);
  const [search, setSearch] = useState({ ANALYST: '', TEAM: '' });
  const pending = useRef(new Map());
  const baseline = useRef(new Map());
  const grids = useRef({});
  const mounted = useRef(true);
  const busyRef = useRef(false);

  // keepPending re-applies the fields the user changed over the fresh rows
  // (after a version conflict) so they can review and save again.
  const load = useCallback(async (keepPending = false) => {
    const response = await fetchRatingsInputs();
    if (!mounted.current) return;
    const entries = response.data.entries;
    const before = baseline.current;
    baseline.current = new Map(entries.map((row) => [row.INPUT_ID, { ...row }]));
    if (!keepPending) {
      setRows(entries);
      return;
    }
    pending.current.forEach((edit, id) => {
      const loaded = before.get(id);
      const fresh = baseline.current.get(id);
      const rebased = rebaseEdit(edit, loaded && inputRecord(loaded), fresh && inputRecord(fresh));
      if (fresh && JSON.stringify(rebased) === JSON.stringify(inputRecord(fresh)))
        pending.current.delete(id);
      else pending.current.set(id, rebased);
    });
    setPendingCount(pending.current.size);
    const unsavedNew = [...pending.current.values()].filter(
      (row) => !baseline.current.has(row.INPUT_ID)
    );
    setRows([
      ...entries.map((row) => ({ ...row, ...pending.current.get(row.INPUT_ID) })),
      ...unsavedNew,
    ]);
  }, []);

  const refresh = useCallback(async (discardConfirmed = false) => {
    Object.values(grids.current).forEach((api) => api.stopEditing());
    if (busyRef.current) return;
    if (pending.current.size && !discardConfirmed) {
      setConfirmation('refresh');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await load();
      pending.current.clear();
      setPendingCount(0);
    } catch (failure) {
      if (mounted.current) setError(errorMessage(failure));
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [load]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const beforeUnload = (event) => {
      if (pending.current.size) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      mounted.current = false;
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [refresh]);

  const add = (kind) => {
    const row = {
      INPUT_ID: crypto.randomUUID(),
      KIND: kind,
      VALUE: '',
      ABBREVIATION: null,
      IS_ACTIVE: true,
    };
    pending.current.set(row.INPUT_ID, inputRecord(row));
    setPendingCount(pending.current.size);
    setRows((current) => [...current, row]);
  };

  const change = ({ data, colDef, oldValue, api }) => {
    if (
      !canWrite ||
      busyRef.current ||
      !['VALUE', 'ABBREVIATION', 'IS_ACTIVE'].includes(colDef.field)
    ) {
      data[colDef.field] = oldValue;
      api?.refreshCells();
      return;
    }
    const value = inputRecord(data);
    const savedRow = baseline.current.get(data.INPUT_ID);
    if (savedRow && JSON.stringify(value) === JSON.stringify(inputRecord(savedRow)))
      pending.current.delete(data.INPUT_ID);
    else pending.current.set(data.INPUT_ID, value);
    setRows((current) =>
      current.map((row) => (row.INPUT_ID === data.INPUT_ID ? { ...data } : row))
    );
    setPendingCount(pending.current.size);
  };

  const requestDiscard = () => {
    Object.values(grids.current).forEach((api) => api.stopEditing());
    if (!canWrite || busyRef.current || !pending.current.size) return;
    setConfirmation('discard');
  };

  const confirmDiscard = () => {
    const requested = confirmation;
    setConfirmation(null);
    if (!requested || busyRef.current) return;
    if (requested === 'refresh') {
      refresh(true);
      return;
    }
    Object.values(grids.current).forEach((api) => api.stopEditing(true));
    setRows([...baseline.current.values()].map((row) => ({ ...row })));
    pending.current.clear();
    setPendingCount(0);
    setError('');
    setNotice('Unsaved changes discarded.');
  };

  const save = async () => {
    Object.values(grids.current).forEach((api) => api.stopEditing());
    if (!canWrite || busyRef.current || !pending.current.size) return;
    const changes = [...pending.current.values()];
    if (changes.some((row) => !String(row.VALUE || '').trim())) {
      setError('Each Input row needs a name.');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      // The route skips unchanged Inputs, so every pending row is sent with the
      // MODIFIED_DATE it was loaded with, unchanged (null for a new Input).
      const response = await saveRatingsInputs(
        changes.map((row) => withLoadedModifiedDate(row, baseline.current.get(row.INPUT_ID)))
      );
      changes.forEach((row) => {
        baseline.current.set(row.INPUT_ID, { ...baseline.current.get(row.INPUT_ID), ...row });
      });
      pending.current.clear();
      setPendingCount(0);
      setNotice(`Saved ${response.data.saved} Input change(s).`);
      onSaved?.();
      try {
        await load();
      } catch (failure) {
        setError(`Save succeeded, but refresh failed: ${errorMessage(failure)}`);
      }
    } catch (failure) {
      const permissionDenied = getPermissionDeniedMessage(failure);
      if (permissionDenied) {
        setError(`${permissionDenied} Your changes are retained.`);
      } else if (isStaleWrite(failure)) {
        // Reload the latest rows; the edits stay pending for an explicit re-save.
        setError(`${getWriteErrorMessage(failure, VERSION_CONFLICT_MESSAGE)} Your changes are retained; review them and save again.`);
        try {
          await load(true);
        } catch (reloadFailure) {
          if (mounted.current) setError(`Reload after conflict failed: ${errorMessage(reloadFailure)}`);
        }
      } else if (shouldReloadAfterWriteError(failure)) {
        // The save may or may not have committed (502/503/504, no response):
        // reload so the screen shows what was stored. load(true) keeps every
        // edit that the reloaded rows do not already contain.
        setError(`Save outcome unknown. ${getWriteErrorMessage(failure, OUTCOME_UNKNOWN_MESSAGE)} The latest Inputs were reloaded and your unsaved changes are retained; review them before saving again.`);
        try {
          await load(true);
        } catch (reloadFailure) {
          if (mounted.current) {
            setError(`Save outcome unknown, and the reload failed: ${errorMessage(reloadFailure)}. Your changes are retained; refresh to check before saving again.`);
          }
        }
      } else {
        setError(`Save failed; your changes are retained. ${getWriteErrorMessage(failure, failure?.message || 'Request failed.')}`);
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const columns = [
    {
      field: 'VALUE',
      headerName: 'Name',
      tooltipField: 'VALUE',
      flex: 1,
      minWidth: 210,
      editable: canWrite && !busy,
      valueParser: ({ newValue }) => (typeof newValue === 'string' ? newValue.trim() : newValue),
    },
    {
      field: 'ABBREVIATION',
      headerName: 'Abbreviation',
      width: 150,
      editable: canWrite && !busy,
      valueParser: ({ newValue }) =>
        typeof newValue === 'string' ? newValue.trim() || null : newValue,
    },
    {
      field: 'IS_ACTIVE',
      headerName: 'Active',
      width: 120,
      minWidth: 110,
      cellDataType: 'boolean',
      editable: canWrite && !busy,
    },
    {
      field: 'MODIFIED_DATE',
      headerName: 'Modified Date',
      width: 190,
      editable: false,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
    },
    { field: 'MODIFIED_BY', headerName: 'Modified By', width: 240, editable: false },
  ];

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, p: 2, gap: 1 }}
    >
      <Typography variant="h6">Analyst and Team inputs</Typography>
      {!canWrite && (
        <Typography variant="caption">SecMaster editors can maintain these lists.</Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button onClick={() => refresh()} disabled={busy}>
          Refresh inputs
        </Button>
        <Button onClick={save} disabled={!canWrite || busy || !pendingCount}>
          Save inputs
        </Button>
        <Button onClick={requestDiscard} disabled={!canWrite || busy || !rows.length}>
          Discard changes
        </Button>
        <Typography variant="body2">{pendingCount} unsaved Input change(s)</Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {[
          ['ANALYST', 'Analysts', 'Add analyst'],
          ['TEAM', 'Teams', 'Add team'],
        ].map(([kind, label, addLabel]) => (
          <Box
            key={kind}
            sx={{ display: 'flex', flexDirection: 'column', minHeight: 350, minWidth: 0 }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">{label}</Typography>
              <Button onClick={() => add(kind)} disabled={!canWrite || busy}>
                {addLabel}
              </Button>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <PamGrid
                rowData={rows.filter((row) => row.KIND === kind)}
                columnDefs={columns}
                defaultColDef={{
                  filter: true,
                  sortable: true,
                  resizable: true,
                  editable: false,
                  wrapHeaderText: true,
                  autoHeaderHeight: true,
                }}
                getRowId={(row) => row.INPUT_ID}
                // PamGrid renders its quick filter even with the toolbar hidden,
                // and calls onQuickFilterChange on every keystroke.
                quickFilterText={search[kind]}
                onQuickFilterChange={(text) => setSearch((current) => ({ ...current, [kind]: text }))}
                onCellValueChanged={change}
                onGridReady={({ api }) => {
                  grids.current[kind] = api;
                }}
                height="100%"
                containerSx={{ height: '100%' }}
                gridContainerSx={{ height: '100%' }}
                showToolbar={false}
                showFooter={false}
                showExport={false}
                isLoading={busy}
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Dialog
        open={Boolean(confirmation)}
        onClose={() => setConfirmation(null)}
        aria-labelledby="inputs-discard-title"
      >
        <DialogTitle id="inputs-discard-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmation === 'refresh'
              ? 'Your unsaved Input changes will be discarded when the data reloads.'
              : 'Analyst and team inputs will return to their last saved values. Unsaved new rows will be removed.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setConfirmation(null)}>Keep editing</Button>
          <Button color="error" onClick={confirmDiscard}>Discard changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
